import React, { useState, useEffect, useRef } from "react";
import { FaMicrophone, FaPaperPlane } from "react-icons/fa6";
import { LuUserSearch } from "react-icons/lu";
import { SiGoogleassistant } from "react-icons/si";
import { PiWaveformBold } from "react-icons/pi";
import logo from "./utils/logo2.png";
import Orb from "./Orb";
import AddQuestionModal from "./AddQuestionModal";
// import { franc } from "franc-min";
import LoginModal from "./LoginModal";
import { getToken } from "./auth";


const BACKEND_URL = "https://parkengage.ai/api";

// ✅ Add your real API keys here (this is what you asked for: frontend use)
const ELEVENLABS_API_KEY = "sk_2171a2b554601826c8a4c69fe2627f92a3ba72c06840ceec";
const ELEVENLABS_VOICE_ID = "Xb7hH8MSUJpSbSDYk0k2";

let sharedAudioCtx = null;


const ChatbotWidget = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [started, setStarted] = useState(false);
  const [showAnimation, setShowAnimation] = useState(false);
  const [listening, setListening] = useState(false);
  const [sessionId] = useState(() => crypto.randomUUID());
  const recognitionRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const deepgramSocketRef = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);


  useEffect(() => {
    const unlock = () => {
      if (!sharedAudioCtx) {
        sharedAudioCtx = new (window.AudioContext || window.webkitAudioContext)();
        console.log("🔓 Global AudioContext created");
      }
      if (sharedAudioCtx.state === "suspended") {
        sharedAudioCtx.resume();
        console.log("🎧 AudioContext resumed for iOS");
      }
    };

    window.addEventListener("touchstart", unlock, { once: true });
    window.addEventListener("click", unlock, { once: true });

    return () => {
      window.removeEventListener("touchstart", unlock);
      window.removeEventListener("click", unlock);
    };
  }, []);


  const isIOS = () =>
    /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  
  // ✅ MAIN SpeechRecognition setup (Web Speech API)
  useEffect(() => {
  if (isIOS()) return;

  const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!SR) return;

  const rec = new SR();
  rec.continuous = false;
  rec.interimResults = true;
  rec.lang = "en-US";

  let detectedLang = "en-US";
  let committedFinal = false;

  rec.onresult = (event) => {
    const text = event.results[0][0].transcript.trim();

    // Only detect when NOT final and enough words
   rec.onresult = (event) => {
  const text = event.results[0][0].transcript.trim();
  if (event.results[0].isFinal) {
    setInput(text);
    recognitionRef.current.detectedLang = "en-US"; // default; will update after backend reply
  }
};


    if (event.results[0].isFinal) {
      committedFinal = true;
      setInput(text);
      recognitionRef.current.detectedLang = detectedLang;
    }
  };

  rec.onerror = () => setListening(false);
  rec.onend = () => setListening(false);

  recognitionRef.current = rec;
}, []);





  // ----- iOS Recording Helpers -----
  // ----- iOS Recording Helpers (Replaced with secure low-latency Deepgram) -----
  const startIOSRecording = async () => {
    try {
      setListening(true);

      // ✅ Get temporary token from backend (no master key on frontend)
      const { token } = await fetch(`${BACKEND_URL}/get-deepgram-token`).then(r => r.json());

      // ✅ Create Deepgram WebSocket authenticated using token (NO query params, NO encoding)
      const ws = new WebSocket(
        "wss://api.deepgram.com/v1/listen?model=nova&punctuate=true",
        ["token", token]
      );
      deepgramSocketRef.current = ws;
      ws.binaryType = "arraybuffer";
      ws.finalTranscript = "";

      // ✅ WAIT for WebSocket to open BEFORE recording audio
      await new Promise((resolve, reject) => {
        ws.onopen = resolve;
        ws.onerror = reject;
      });

      // ✅ Request Microphone
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // ✅ Use mp4 (iOS friendly, Deepgram auto-detects codec)
      const recorder = new MediaRecorder(stream, { mimeType: "audio/mp4" });
      mediaRecorderRef.current = recorder;

      let chunks = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };

      // ✅ Capture final transcript only (no partials spam)
      ws.onmessage = (m) => {
        const data = JSON.parse(m.data);
        const t = data?.channel?.alternatives?.[0]?.transcript?.trim();
        if (t) ws.finalTranscript = t;
      };

      recorder.onstop = async () => {
        try {
          if (ws.readyState === WebSocket.OPEN) {
            for (const chunk of chunks) {
              ws.send(await chunk.arrayBuffer());
            }
            ws.send(JSON.stringify({ type: "CloseStream" }));
            await new Promise(r => setTimeout(r, 200));
            ws.close();
          }
        } catch (err) {
          console.error("iOS Deepgram send/close error:", err);
        }

        setInput(ws.finalTranscript || "");
        chunks = [];
        setListening(false);
      };

      recorder.start();
    } catch (err) {
      console.error("❌ startIOSRecording FAILED:", err);
      setListening(false);
    }
  };

  const stopIOSRecording = () => {
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }
    } catch (err) {
      console.error("🛑 stopIOSRecording FAILED:", err);
    }
  };




  const speakIOS = async (text) => {
    try {
      stopIOSRecording(); // pause STT while speaking

      const res = await fetch(
        `https://api.elevenlabs.io/v1/text-to-speech/${ELEVENLABS_VOICE_ID}?optimize_streaming_latency=3`,
        {
          method: "POST",
          headers: {
            "xi-api-key": ELEVENLABS_API_KEY,
            "Content-Type": "application/json",
            Accept: "audio/mpeg",
          },
          body: JSON.stringify({ text, model_id: "eleven_turbo_v2" }),
        }
      );

      const audioData = await res.arrayBuffer();

      if (!sharedAudioCtx)
        sharedAudioCtx = new (window.AudioContext || window.webkitAudioContext)();

      await sharedAudioCtx.resume();

      const buffer = await sharedAudioCtx.decodeAudioData(audioData);
      const source = sharedAudioCtx.createBufferSource();
      source.buffer = buffer;
      source.connect(sharedAudioCtx.destination);
      source.start();

      source.onended = () => {
        console.log("✅ iOS TTS finished → STT remains OFF until user holds mic");
        setListening(false);
      };

    } catch (err) {
      console.error("❌ speakIOS failed:", err);
    }
  };





  const speak = (text, langOverride = null) => {

    if (!text) return;

    // iOS → use ElevenLabs workflow above
    if (isIOS()) return speakIOS(text);

    if (!window.speechSynthesis) return;

    // Stop STT during TTS
    if (recognitionRef.current && listening) {
      try {
        recognitionRef.current.stop();
      } catch { }
      setListening(false);
      console.log("🤫 STT paused during TTS...");
    }

    const utter = new SpeechSynthesisUtterance(text);

 

    // Use the new, improved detector (handles Romanized Hindi)
const langCode = langOverride || "en-US";

utter.lang = langCode;



    // 🔹 Detect Browser
    const ua = navigator.userAgent;
    const isEdge = /Edg/i.test(ua);
    const isChrome = /Chrome/i.test(ua) && !isEdge && !/Safari/i.test(ua);
    const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
    const isMac = navigator.platform.toLowerCase().includes("mac");

    // 🔹 Voice Maps (female voices by browser)
    const edgeVoiceMap = {
      "hi-IN": /microsoft swara online.*natural/i,
      "en-US": /microsoft aria online.*natural/i,
      "es-ES": /microsoft elvira online.*natural/i,
      "fr-FR": /microsoft denise online.*natural/i,
    };

    const chromeVoiceMap = {
      "hi-IN": /google.*hindi/i,
      "en-US": /google us english female|google uk english female/i,
      "es-ES": /google español/i,
      "fr-FR": /google français/i,
    };

    const safariVoiceMap = {
      "hi-IN": /lekha/i,
      "en-US": /samantha/i,
      "es-ES": /monica|paulina/i,
      "fr-FR": /amelie|thomas/i,
    };

    const selectVoice = () => {
      const voices = window.speechSynthesis.getVoices();
      let selectedVoice = null;

      let voiceMap;
      if (isSafari && isMac) voiceMap = safariVoiceMap;
      else if (isEdge) voiceMap = edgeVoiceMap;
      else if (isChrome) voiceMap = chromeVoiceMap;

      if (voiceMap && voiceMap[langCode]) {
        selectedVoice = voices.find((v) => voiceMap[langCode].test(v.name));
      }
      console.log(window.speechSynthesis.getVoices().map(v => v.name));

      // 🩷 Fallback — female-sounding or English voice
      if (!selectedVoice) {
        selectedVoice =
          voices.find(
            (v) => v.lang === langCode && /female|woman|girl|lady/i.test(v.name)
          ) ||
          voices.find((v) =>
            /samantha|aria|zira|olivia|emma|hazel|victoria/i.test(v.name)
          ) ||
          voices.find((v) => /en(-|_)us/i.test(v.lang)) ||
          voices[0];
      }

      if (selectedVoice) {
        utter.voice = selectedVoice;
        console.log(`🎙️ Speaking (${langCode}) with:`, selectedVoice.name);
      } else {
        console.warn("⚠️ No matching female voice found — using default system voice.");
      }

      // 🔹 Adjust tone for natural female sound
      utter.pitch = 1.1;
      utter.rate = 1.0;
      if (/\?$/.test(text)) {
        utter.pitch = 1.2;
        utter.rate = 1.05;
      }

      utter.onend = () => {
        console.log("✅ Web TTS finished → STT remains OFF until user holds mic");
        setListening(false);
      };

      window.speechSynthesis.cancel(); // prevents overlapping voices
      window.speechSynthesis.speak(utter);
    };

    // Wait for voices to load
    if (speechSynthesis.getVoices().length === 0) {
      window.speechSynthesis.onvoiceschanged = () => {
        window.speechSynthesis.onvoiceschanged = null;
        selectVoice();
      };
    } else {
      selectVoice();
    }
  };



  const handleSend = async (e, voiceText = null) => {
    if (e) e.preventDefault();
    const question = (voiceText || input).trim();
    if (!question) return;

    setStarted(true);
    setMessages((prev) => [...prev, { text: question, sender: "user" }]);
    setInput("");

    if (inputRef.current) {
      inputRef.current.style.height = "auto";
      inputRef.current.style.height = "24px";
    }

    try {
      const res = await fetch(`${BACKEND_URL}/query`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          question,
          session_id: sessionId,
          meta: { garage: "default_garage" },
          lang: "en-US",
        }),
      });

      const data = await res.json();
  const answer = data.text || "Sorry, I couldn’t get that.";
setMessages((prev) => [...prev, { text: answer, sender: "ai" }]);

// 🧠 Detect the language from the user's question (handles Devanagari + Romanized Hindi)
const backendLang = data.lang || "en-US";
if (recognitionRef.current) recognitionRef.current.detectedLang = backendLang;
speak(answer, backendLang);


    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { text: "⚠️ Server error. Please try again.", sender: "ai" },
      ]);
    }
  };

  const handleIconClick = () => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.onresult = null;
        recognitionRef.current.stop();
      } catch { }
    }
    if (window.speechSynthesis.speaking) {
      window.speechSynthesis.cancel();
    }
    setShowAnimation(true);
  };

  return (
    <div className="flex flex-col min-h-screen bg-white relative px-4 sm:px-6 md:px-10 py-6 sm:py-8">
      <div
        className="
          absolute top-4 sm:top-5 md:top-6 
          left-1/2 transform -translate-x-1/2
          lg:left-10 lg:top-6 lg:transform-none
        "
      >
        <img
          src={logo}
          alt="ParkEngage Logo"
          className="h-10 sm:h-12 md:h-14 w-auto object-contain transition-all duration-300"
        />
      </div>

      {/* <button
        onClick={() => setAddModalOpen(true)}
        className="absolute top-4 right-4 sm:top-5 md:top-6 bg-blue-600 text-white text-sm sm:text-base px-3 py-2 rounded-full shadow hover:bg-blue-700 active:scale-95 transition"
        title="Add Question"
      >
        Add Q&A
      </button> */}

      <button
        onClick={() => {
          if (getToken()) {
            setAddModalOpen(true);
          } else {
            setLoginOpen(true);
          }
        }}
        className="absolute top-4 right-4 sm:top-5 md:top-6
             text-blue-600 text-sm sm:text-base font-semibold
             underline hover:text-blue-800 transition cursor-pointer bg-transparent shadow-none px-0 py-0"
        title="Add Question"
      >
        Add Q&A
      </button>


      {addModalOpen && <AddQuestionModal onClose={() => setAddModalOpen(false)} />}
      {loginOpen && <LoginModal onClose={() => setLoginOpen(false)} onLoginSuccess={() => setAddModalOpen(true)} />}

      <div className="flex flex-col items-center justify-center flex-1 mt-10 lg:mt-6">
        {showAnimation ? (
          <Orb onClose={() => setShowAnimation(false)} />
        ) : (
          <>
            {!started && (
              <h1
                className="text-xl sm:text-2xl md:text-3xl lg:text-2xl font-semibold text-center px-4 mb-2 sm:mb-3"
                style={{ color: "#305688ff" }}
              >
                Experience true digital parking with{" "}
                <span className="font-bold text-blue-400">ParkEngage</span>
              </h1>
            )}

            <div
              className={`flex flex-col w-full max-w-md sm:max-w-2xl md:max-w-3xl 
                ${started ? "h-[70vh] sm:h-[76vh]" : "h-auto"} 
                mt-2
                border border-gray-200 rounded-2xl shadow-lg bg-white overflow-hidden 
                transition-all duration-500`}
            >
              {started && (
                <div className="flex-1 overflow-y-auto p-5 sm:p-7 space-y-4 bg-gray-50">
                  {messages.map((msg, index) => (
                    <div
                      key={index}
                      className={`flex items-start ${msg.sender === "user" ? "justify-end" : "justify-start"
                        }`}
                    >
                      <div
                        className={`${msg.sender === "user" ? "order-2 ml-2" : "mr-2"
                          } flex items-center`}
                      >
                        {msg.sender === "user" ? (
                          <LuUserSearch className="text-[#174A8C] text-xl sm:text-2xl md:text-3xl lg:text-base" />
                        ) : (
                          <SiGoogleassistant className="text-gray-800 text-xl sm:text-2xl md:text-3xl lg:text-base" />
                        )}
                      </div>

                      <div
                        className={`px-4 py-2 rounded-xl max-w-[80%] text-base sm:text-lg md:text-xl lg:text-sm break-words whitespace-pre-wrap ${msg.sender === "user"
                          ? "bg-[#e3f2fd] text-[#174A8C]"
                          : "bg-white text-gray-800 border border-gray-200"
                          }`}
                      >
                        {msg.text}
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              )}

              <form
                onSubmit={handleSend}
                className="flex items-center w-full px-3 py-2 sm:px-5 sm:py-3 border-t border-gray-200 bg-white"
              >
                <div className="flex items-end w-full bg-gray-100 rounded-md px-3 py-1.5 sm:px-4 sm:py-2 shadow-md">
                  <textarea
                    ref={inputRef}
                    className="flex-1 bg-transparent outline-none text-gray-800 placeholder-gray-500 resize-none overflow-hidden transition-[height] duration-200 text-base sm:text-lg md:text-xl lg:text-sm"
                    placeholder="Ask anything..."
                    value={input}
                    onChange={(e) => {
                      setInput(e.target.value);
                      e.target.style.height = "auto";
                      e.target.style.height = `${e.target.scrollHeight}px`;
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSend(e);
                      }
                    }}
                    rows={1}
                    style={{ maxHeight: "120px" }}
                  />

                  {input.trim() ? (
                    <button
                      type="submit"
                      className="text-[#174A8C] text-xl sm:text-2xl md:text-3xl lg:text-base mx-2 hover:scale-110 transition-transform"
                      title="Send"
                    >
                      <FaPaperPlane />
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        className={`text-[#174A8C] text-xl sm:text-2xl md:text-3xl lg:text-base mx-2 transition-transform ${listening
                          ? "text-red-600 animate-pulse scale-110"
                          : "hover:scale-110"
                          }`}
                        title="Hold to Talk"
                        onMouseDown={() => {
                          if (isIOS()) startIOSRecording();
                          else if (recognitionRef.current && !listening) {
                            recognitionRef.current.start();
                          }
                          setListening(true);
                        }}
                        onMouseUp={() => {
                          if (isIOS()) stopIOSRecording();
                          else if (recognitionRef.current && listening) {
                            recognitionRef.current.stop();
                          }
                          setListening(false);
                        }}
                        onTouchStart={() => {
                          if (isIOS()) startIOSRecording();
                          else if (recognitionRef.current && !listening) {
                            recognitionRef.current.start();
                          }
                          setListening(true);
                        }}
                        onTouchEnd={() => {
                          if (isIOS()) stopIOSRecording();
                          else if (recognitionRef.current && listening) {
                            recognitionRef.current.stop();
                          }
                          setListening(false);
                        }}
                      >
                        <FaMicrophone />
                      </button>

                      <button
                        type="button"
                        className="text-[#174A8C] text-xl sm:text-2xl md:text-3xl lg:text-base hover:scale-110 transition-transform"
                        title="Go to Chat Animation"
                        onClick={handleIconClick}
                      >
                        <PiWaveformBold />
                      </button>
                    </>
                  )}
                </div>
              </form>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ChatbotWidget;
